import {
  getLicensedModules,
  WitboostModule,
  WitboostCommercialModule,
  mergeArraysWithEmptyCheck,
  getModuleOverrideEnabledFromConfig,
} from '@agilelab/plugin-wb-platform-common';
import {
  builderSoftwareCatalogView,
  cgpEntityEdit,
  cgpEntityView,
  CompletePermission,
  platformCustomViewEditPermission,
  platformExtensionManagerEditPermission,
  platformSettingsEditPermission,
  platformWebhookEditPermission,
  practiceShaperEditPermission,
  UserPermissionsMap,
} from '@agilelab/plugin-wb-rbac-common';
import {
  configApiRef,
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@mainstage/core-plugin-api';
import {
  catalogEntityCreatePermission,
  catalogEntityReadPermission,
  catalogLocationCreatePermission,
  catalogLocationReadPermission,
} from '@mainstage/plugin-catalog-common/alpha';
import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useCallback,
  useMemo,
  useState,
} from 'react';
import useAsync from 'react-use/lib/useAsync';
import { useIsGuestUser } from '../hooks';
import { infoApiRef, PlatformInfo } from '../apis';

export interface Features {
  meshSupervisionEnabled: boolean;
  governanceEnabled: boolean;
  catalogEnabled: boolean;
  templatesEnabled: boolean;
  blueprintsEnabled: boolean;
  builderEnabled: boolean;
  softwareCatalogEnabled: boolean;
  platformSettingsEnabled: boolean;
  platformSupervisionEnabled: boolean;
  practiceShaperEnabled: boolean;
  customViewEditEnabled: boolean;
  extensionManagerEditEnabled: boolean;
  extensionManagerTechAdaptersEditEnabled: boolean;
  extensionManagerTechnologiesEditEnabled: boolean;
  extensionManagerSectionEnabled: boolean;
  webhooksEditEnabled: boolean;
  moduleOverridePageEnabled: boolean;
  dataContractsPageEnabled: boolean;
  marketplaceCatalogEnabled: boolean;
  marketplaceGraphEnabled: boolean;
  marketplaceEnabled: boolean;
  prototypePageEnabled: boolean;
  notificationsMenuEnabled: boolean;
  dataContractsOverviewPageEnabled: boolean;
  businessConceptMapEnabled: boolean;
  wittyAssistantEnabled: boolean;
  wittyAutofixEnabled: boolean;
  accessControlEnabled: boolean;
  platformInfo: PlatformInfo | undefined;
}

export interface EnabledFeaturesType {
  loading: boolean;
  error: Error | undefined;
  data: Features;
  isModuleLicensed: (module: WitboostModule) => boolean;
  isCommercialModuleLicensed: (module: WitboostCommercialModule) => boolean;
}

export const EnabledFeatures = createContext<EnabledFeaturesType>(
  {} as EnabledFeaturesType,
);

export interface EnabledFeaturesProviderProps {
  children?: ReactNode;
}

export const EnabledFeaturesProvider: FC<EnabledFeaturesProviderProps> = ({
  children,
}) => {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const infoApi = useApi(infoApiRef);

  const permissionsEnabled =
    configApi.getOptionalBoolean('permission.enabled') ?? false;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [permissions, setPermissions] = useState<UserPermissionsMap>(
    UserPermissionsMap.fromPermissions([]),
  );

  const moduleOverrideEnabled = getModuleOverrideEnabledFromConfig(configApi);

  const [platformInfo, setPlatformInfo] = useState<PlatformInfo | undefined>(
    undefined,
  );

  useAsync(async () => {
    try {
      const identity = await identityApi.getCredentials();

      const rbacUrl = `${await discoveryApi.getBaseUrl('rbac')}/permissions`;

      const response = await fetchApi.fetch(rbacUrl, {
        headers: identity.token
          ? { authorization: `Bearer ${identity.token}` }
          : {},
      });

      const data = (await response.json()) as {
        userPermissions: CompletePermission[];
      };

      setPermissions(UserPermissionsMap.fromPermissions(data.userPermissions));

      const info = await infoApi.getInfo({
        token: identity.token,
      });
      // Store commercial modules directly for simpler checks
      setPlatformInfo(info);

      setLoading(false);
    } catch (fetchError) {
      setError(fetchError as Error);
      setLoading(false);
    }
  }, [discoveryApi, identityApi]);

  const { data: isGuestLogin } = useIsGuestUser();
  const noGuestLogin = !isGuestLogin;

  // Derive licensed commercial modules from platform info
  const licensedCommercialModules = useMemo(() => {
    if (!platformInfo) return [];
    if (moduleOverrideEnabled) {
      return platformInfo.license.activeModules as WitboostCommercialModule[];
    }
    return platformInfo.license.modules as WitboostCommercialModule[];
  }, [platformInfo, moduleOverrideEnabled]);

  // Derive internal Witboost modules from the stored commercial modules
  const derivedModules = useMemo(
    () =>
      getLicensedModules({
        license: {
          modules: licensedCommercialModules,
        },
      }),
    [licensedCommercialModules],
  );

  const enabled = {
    config: {
      modules: derivedModules,
      meshSupervision:
        configApi.getOptionalBoolean(
          'mesh.marketplace.ui.meshSupervisionPage.enabled',
        ) ?? true,

      platformSupervision:
        configApi.getOptionalBoolean('supervisionDemo.enabled') ?? false,

      prototypePageEnabled:
        configApi.getOptionalBoolean('mesh.builder.ui.prototypePage.enabled') ??
        false,

      blueprints:
        configApi.getOptionalBoolean(
          'mesh.builder.scaffolder.blueprints.enabled',
        ) ?? true,

      platformSettings:
        configApi.getOptionalBoolean('mesh.platformSettings.enabled') ?? false,

      dataContractsPage:
        configApi.getOptionalBoolean(
          'mesh.marketplace.ui.dataContracts.page.enabled',
        ) ?? false,

      dataContractsOverviewPage:
        configApi.getOptionalBoolean(
          'mesh.marketplace.ui.dataContractsOverview.page.enabled',
        ) ?? false,

      marketplaceEnabled:
        configApi.getOptionalBoolean('mesh.marketplace.enabled') ?? true,

      wittyAssistantEnabled:
        configApi.getOptionalBoolean('witty.assistant.enabled') ?? false,

      wittyAutofixEnabled:
        configApi.getOptionalBoolean(
          'witty.experimental.autofixAgent.enabled',
        ) ?? false,
      businessConceptMapEnabled:
        configApi.getOptionalBoolean(
          'mesh.marketplace.businessConceptMap.enabled',
        ) ?? false,
    },

    permission: {
      templates:
        !permissionsEnabled ||
        permissions.hasPermission(catalogEntityCreatePermission.name) ||
        permissions.hasPermission(catalogLocationCreatePermission.name),

      catalog:
        !permissionsEnabled ||
        permissions.hasPermission(catalogEntityReadPermission.name) ||
        permissions.hasPermission(catalogLocationReadPermission.name),

      platformSettings:
        !permissionsEnabled ||
        permissions.hasPermission(platformSettingsEditPermission.name),

      practiceShaper:
        !permissionsEnabled ||
        permissions.hasPermission(practiceShaperEditPermission.name),

      governance:
        !permissionsEnabled ||
        permissions.hasPermission(cgpEntityEdit.name) ||
        permissions.hasPermission(cgpEntityView.name),

      softwareCatalog:
        !permissionsEnabled ||
        permissions.hasPermission(builderSoftwareCatalogView.name),
      customViewEdit:
        !permissionsEnabled ||
        permissions.hasPermission(platformCustomViewEditPermission.name),
      extensionManagerEdit:
        !permissionsEnabled ||
        permissions.hasPermission(platformExtensionManagerEditPermission.name),
      webhooksEdit:
        !permissionsEnabled ||
        permissions.hasPermission(platformWebhookEditPermission.name),
    },
  };

  const isModuleLicensed = useCallback(
    (module: WitboostModule): boolean => derivedModules.includes(module),
    [derivedModules],
  );

  const licensedCommercialModulesSet = useMemo(
    () => new Set<WitboostCommercialModule>(licensedCommercialModules),
    [licensedCommercialModules],
  );

  const isCommercialModuleLicensed = useCallback(
    (commercialModule: WitboostCommercialModule): boolean =>
      licensedCommercialModulesSet.has(commercialModule),
    [licensedCommercialModulesSet],
  );

  // Marketplace sidebar
  const marketplaceCatalogEnabled =
    enabled.config.modules.includes(WitboostModule.MARKETPLACE) && noGuestLogin;
  const marketplaceGraphEnabled =
    enabled.config.modules.includes(WitboostModule.MARKETPLACE) && noGuestLogin;

  const dataContractsPageEnabled =
    enabled.config.modules.includes(WitboostModule.MARKETPLACE) &&
    noGuestLogin &&
    enabled.config.dataContractsPage;

  const marketplaceEnabled =
    enabled.config.modules.includes(WitboostModule.MARKETPLACE) &&
    enabled.config.marketplaceEnabled &&
    (marketplaceGraphEnabled ||
      marketplaceCatalogEnabled ||
      dataContractsPageEnabled);

  const dataContractsOverviewPageEnabled =
    enabled.config.modules.includes(WitboostModule.MARKETPLACE) &&
    noGuestLogin &&
    enabled.config.dataContractsOverviewPage;

  // Builder sidebar
  const catalogEnabled =
    enabled.config.modules.includes(WitboostModule.BUILDER) &&
    noGuestLogin &&
    enabled.permission.catalog;
  const templatesEnabled =
    enabled.config.modules.includes(WitboostModule.BUILDER) &&
    noGuestLogin &&
    enabled.permission.templates;
  const blueprintsEnabled =
    enabled.config.modules.includes(WitboostModule.BUILDER) &&
    noGuestLogin &&
    enabled.permission.templates &&
    enabled.config.blueprints;
  const builderEnabled =
    (enabled.config.modules.includes(WitboostModule.BUILDER) &&
      catalogEnabled) ||
    templatesEnabled ||
    blueprintsEnabled;
  const softwareCatalogEnabled =
    enabled.config.modules.includes(WitboostModule.BUILDER) &&
    enabled.permission.softwareCatalog;
  const prototypePageEnabled =
    enabled.config.modules.includes(WitboostModule.BUILDER) &&
    enabled.config.prototypePageEnabled;

  // Governance sidebar
  const governanceEnabled =
    enabled.config.modules.includes(WitboostModule.CGP) &&
    noGuestLogin &&
    enabled.permission.governance;

  // Notifications and Settings sidebar
  // Mesh Supervision is Marketplace-only
  const meshSupervisionEnabled =
    enabled.config.modules.includes(WitboostModule.MARKETPLACE) &&
    noGuestLogin &&
    enabled.config.meshSupervision;
  // Practice Shaper is Control Plane related
  const practiceShaperEnabled =
    enabled.config.modules.includes(WitboostModule.PROVISIONING) &&
    noGuestLogin &&
    enabled.permission.practiceShaper;

  const customViewEditEnabled =
    enabled.config.modules.includes(WitboostModule.ADMIN) &&
    noGuestLogin &&
    enabled.permission.customViewEdit;

  // Tech Adapters are Control Plane related
  const techAdaptersRequiredModules = [WitboostModule.PROVISIONING];

  // Technologies are Control Plane related
  const technologiesRequiredModules = [WitboostModule.PROVISIONING];

  const webhooksRequiredModules: WitboostModule[] = [];

  const hasRequiredModules = (requiredModules: WitboostModule[]): boolean => {
    if (requiredModules.length === 0) {
      return true; // No requirements means always pass
    }
    return requiredModules.some(module =>
      enabled.config.modules.includes(module),
    );
  };

  const extensionManagerSectionRequiredModules = mergeArraysWithEmptyCheck(
    techAdaptersRequiredModules,
    technologiesRequiredModules,
    webhooksRequiredModules,
  );

  const extensionManagerEditEnabled =
    noGuestLogin && enabled.permission.extensionManagerEdit;

  const extensionManagerTechAdaptersEditEnabled =
    hasRequiredModules(techAdaptersRequiredModules) &&
    noGuestLogin &&
    enabled.permission.extensionManagerEdit;

  const extensionManagerTechnologiesEditEnabled =
    hasRequiredModules(technologiesRequiredModules) &&
    noGuestLogin &&
    enabled.permission.extensionManagerEdit;

  const webhooksEditEnabled =
    hasRequiredModules(webhooksRequiredModules) &&
    noGuestLogin &&
    (enabled.permission.webhooksEdit ||
      enabled.permission.extensionManagerEdit);

  // Used to determine if the Extension Manager section is enabled in the sidebar
  const extensionManagerSectionEnabled =
    hasRequiredModules(extensionManagerSectionRequiredModules) &&
    noGuestLogin &&
    (extensionManagerEditEnabled ||
      extensionManagerTechAdaptersEditEnabled ||
      extensionManagerTechnologiesEditEnabled ||
      webhooksEditEnabled);

  const moduleOverridePageEnabled =
    enabled.config.modules.includes(WitboostModule.ADMIN) &&
    moduleOverrideEnabled &&
    noGuestLogin;

  const platformSupervisionEnabled =
    enabled.config.modules.includes(WitboostModule.ADMIN) &&
    noGuestLogin &&
    enabled.config.platformSupervision;

  const notificationsMenuEnabled = noGuestLogin;
  const platformSettingsEnabled =
    enabled.config.modules.includes(WitboostModule.ADMIN) &&
    noGuestLogin &&
    enabled.config.platformSettings &&
    enabled.permission.platformSettings;

  const businessConceptMapEnabled =
    enabled.config.modules.includes(WitboostModule.MARKETPLACE) &&
    enabled.config.businessConceptMapEnabled;

  const wittyAssistantEnabled =
    enabled.config.modules.includes(WitboostModule.WITTY) &&
    enabled.config.wittyAssistantEnabled;

  const wittyAutofixEnabled =
    enabled.config.modules.includes(WitboostModule.WITTY) &&
    enabled.config.wittyAutofixEnabled;

  const accessControlEnabled =
    enabled.config.modules.includes(WitboostModule.MARKETPLACE) && noGuestLogin;

  const contextValue = useMemo(
    () => ({
      loading,
      error,
      data: {
        meshSupervisionEnabled,
        platformSupervisionEnabled,
        practiceShaperEnabled,
        customViewEditEnabled,
        extensionManagerEditEnabled,
        extensionManagerTechAdaptersEditEnabled,
        extensionManagerTechnologiesEditEnabled,
        extensionManagerSectionEnabled,
        webhooksEditEnabled,
        governanceEnabled,
        catalogEnabled,
        templatesEnabled,
        blueprintsEnabled,
        builderEnabled,
        softwareCatalogEnabled,
        prototypePageEnabled,
        platformSettingsEnabled,
        dataContractsPageEnabled,
        marketplaceCatalogEnabled,
        marketplaceGraphEnabled,
        marketplaceEnabled,
        moduleOverridePageEnabled,
        notificationsMenuEnabled,
        dataContractsOverviewPageEnabled,
        businessConceptMapEnabled,
        wittyAssistantEnabled,
        wittyAutofixEnabled,
        accessControlEnabled,
        platformInfo,
      },
      isModuleLicensed,
      isCommercialModuleLicensed,
    }),
    [
      loading,
      error,
      meshSupervisionEnabled,
      platformSupervisionEnabled,
      practiceShaperEnabled,
      customViewEditEnabled,
      extensionManagerEditEnabled,
      extensionManagerTechAdaptersEditEnabled,
      extensionManagerTechnologiesEditEnabled,
      extensionManagerSectionEnabled,
      webhooksEditEnabled,
      moduleOverridePageEnabled,
      governanceEnabled,
      catalogEnabled,
      templatesEnabled,
      blueprintsEnabled,
      builderEnabled,
      softwareCatalogEnabled,
      prototypePageEnabled,
      platformSettingsEnabled,
      dataContractsPageEnabled,
      marketplaceCatalogEnabled,
      marketplaceGraphEnabled,
      marketplaceEnabled,
      notificationsMenuEnabled,
      dataContractsOverviewPageEnabled,
      businessConceptMapEnabled,
      wittyAssistantEnabled,
      wittyAutofixEnabled,
      accessControlEnabled,
      platformInfo,
      isModuleLicensed,
      isCommercialModuleLicensed,
    ],
  );

  return (
    <EnabledFeatures.Provider value={contextValue}>
      {children}
    </EnabledFeatures.Provider>
  );
};

export const useEnabledFeatures = () => useContext(EnabledFeatures);
